/**************************************************************************************************************************************************
*   MODULE NAME     :  App
*   AUTHOR          :  GLSD DEV Team
*   DATE            :  05-01-2021
*   DESCRIPTION     :  Component to load entire application, hanldle Authentication, Routes
*   Copyright 2021 Eli Lilly and Company
*   All rights reserved
*
/**************************************************************************************************************************************************
*   *** CHANGE HISTORY ***
/**************************************************************************************************************************************************
*   TRACKING NUMBER: CHG1689267
*   DESCRIPTION: <GLSD>: New System Development
**************************************************************************************************************************************************
*   TRACKING NUMBER: CHG2467262
*   DESCRIPTION: <GLSD>: Node 20 Upgrade and Implement MSAL
*   DATE: 29-Jan-2025
/**************************************************************************************************************************************************/
import React, { Component } from "react";
import { connect } from "react-redux";
import { FaSpinner } from 'react-icons/fa';
import { Redirect, Route, Switch, withRouter } from "react-router-dom";
import { bindActionCreators } from "redux";
import { loadLoggedUser, loadAuthProvider } from "./actions/userActions";
import swal from 'sweetalert';
import config from './config.json';
import Footer from "./components/footer/Footer";
import Header from "./components/header/Header";
import NotFound from "./components/common/notFound/NotFound";
import Abstracts from "./components/abstracts/AllAbstracts/Abstracts";
import CreateAbstract from "./components/abstracts/CreateAbstract/CreateAbstract";
import AbstractDetail from "./components/abstracts/AbstractDetail/AbstractDetail";
import UploadXMLFeed from "./components/uploadXMLFeed/UploadXMLFeed";
import Dashboard from "./components/dashboard/Dashboard";
import Search from "./components/search/Search";
import Help from "./components/help/Help";
import {
  loadAllProduct,
  loadAllCountry,
  loadAllExplanation,
  loadAllStatus
} from "./actions/abstractActions";
import { loadUsersData } from "./actions/userActions";
import { loadActiveInactiveUsersData } from "./actions/userActions";
import AllSearchResults from "./components/search/AllSearchResults";
import Metrics from "./components/metrics/Metrics";
import MetricResults from "./components/metrics/MetricResults";
import Administration from "./components/administration/Administration";
import Audit from "./components/audits/Audit";
import DashboardResults from "./components/dashboard/DashboardResults";

import { AuthProvider } from "./auth/index"
import axios from "axios";
import { USER_ACTIVITY_ERROR_MESSAGE } from './components/common/MessagesPool';
import { addAbstractDetails } from "./actions/homeActions";
import SearchResults from "./components/search/SearchResults";
import { createUser } from "./actions/userActions";
import WarningModal from "./components/common/WarningModal";

class App extends Component {
  constructor(props) {
    super(props);
    this.authProvider = null;
    this.state = {
      appEnvironment: process.env.REACT_APP_ENVIROMENT,
      appVersion: process.env.REACT_APP_VERSION,
      isLoading: true,
      restrictUser: true,
      loggedUserDetails: {
        userDetail: {
          firstName: "admin",
          lastName: "singh",
          emailId: "xyz.com",
          userClientId: "C283195",
          isAdmin: false,
          welcomePage: false,
          role: ""
        }
      },
      userActivityDetails: null,
      statusData: [],
      productData: [],
      countryData: [],
      explanationData: [],
      usersData: [],
    };
    this.idle_seconds = 0;
    this.app_has_focus = true;
    this.refreshSeconds = 4 * 60 * 60;
  }

  // Initialize authProvider for authentication
  handleAuthentication = async () => {
    // Config paramaters for authentication
    const config = {
      clientId: '0545c53a-54e3-4810-8a25-f1efc1a1fa75',
      openIdCallback: encodeURI('/auth/callback'),
      isGatewayScopeRequired: true,
      gatewayScopeType: 'non_production',
      logLevel: 'INFO',
    }
    // Initialize authProvider for authentication
    this.authProvider = new AuthProvider(config);
    await this.props.loadAuthProvider(this.authProvider);
    if (window.location.pathname != "/auth/callback") {
      localStorage.setItem("localpathname", window.location.pathname + window.location.search)
    }
    let isUserAuthenticated = await this.authProvider.isLoggedIn();
    if(isUserAuthenticated) {
      this.eventHandler(this.authProvider);
    } else {
      // handle tasks after complete authentication
      this.authProvider.handlePromiseRedirectCallback(() => {
        this.eventHandler(this.authProvider)
      })
    }
  }

  // handle auth tasks and get user details
  async eventHandler(authProvider) {
    const res = await authProvider.getAccessToken();
    const { accessToken, idTokenClaims } = res;
    const bearer = "Bearer " + accessToken;
    let config = {
      headers: {
        "Authorization": bearer
      }
    }

    // Get User details from Lilly graph point api
    const graphEndpoint = "https://graph.microsoft.com/v1.0/me";
    const response = await axios.get(graphEndpoint, config);
    this.loadMasterDataMount()
    this.loadAuthUser(idTokenClaims, response);
  }
  // Save user details in state 
  loadAuthUser = (user, res) => {
    if (
      !res || !res.data || !user || !user.roles || user.roles.length === 0) {
      this.setState({
        restrictUser: true,
      });
      this.setLoader(false);
      return;
    }
    let userName = res.data
    let firstName = userName.givenName;
    let roles = user.roles;
    let userRole = '';
    let siteOfOrigin = '';
    if (roles.includes(`${config.DEV_ROLES.business_admin_row}`)) {
      userRole = "GLSD_Business_Administrator";
      siteOfOrigin = "Global";
    } else if (roles.includes(`${config.DEV_ROLES.business_admin}`)) {
      userRole = "GLSD_Business_Administrator";
      siteOfOrigin = "Japan";
    } else if (roles.includes(`${config.DEV_ROLES.associate_row}`)) {
      userRole = "GLSD_Literature_Associate";
      siteOfOrigin = "Global";
    } else if (roles.includes(`${config.DEV_ROLES.associate}`)) {
      userRole = "GLSD_Literature_Associate";
      siteOfOrigin = "Japan";
    } else if (roles.includes(`${config.DEV_ROLES.viewer_row}`)) {
      userRole = "GLSD_Viewer";
      siteOfOrigin = "Global";
    } else if (roles.includes(`${config.DEV_ROLES.viewer}`)) {
      userRole = "GLSD_Viewer";
      siteOfOrigin = "Japan";
    } else if (roles.includes(`${config.DEV_ROLES.it_admin}`)) {
      userRole = "GLSD_IT_Administrator"
      siteOfOrigin = "None";

    }

    this.props.loadLoggedUser({
      firstName: userName.givenName,
      lastName: userName.surname,
      emailId: user.preferred_username,
      userClientId: user.uid,
      role: userRole,
      roles: roles,
      japanUser: siteOfOrigin === "Japan"
    });

    this.setState(
      {
        loggedUserDetails: {
          userDetail: {
            firstName: userName.givenName,
            lastName: userName.surname,
            emailId: user.preferred_username,
            userClientId: user.uid,
            isAdmin: userRole === "GLSD_IT_Administrator",
            welcomePage: false,
            role: userRole,
            siteOfOrigin: siteOfOrigin
          }
        },
        restrictUser: false
      }, () => {
        this.props.createUser({
          firstName: userName.givenName,
          lastName: userName.surname,
          emailId: user.preferred_username,
          systemId: user.uid,
          activationDate: new Date().toISOString(),
          active: 1,
          createdBy: user.uid,
          role: userRole,
          japanUser: siteOfOrigin.toUpperCase() === "JAPAN"
        })
      }
    );



    this.setLoader(false)
  };
  /**
     * Function of react life cycle
  **/
  componentDidMount() {
this.setRefresher();
    this.handleAuthentication();
  }
  /**
    * Function to reset idle timeout
    */
  resetIdleSeconds = () => (
    this.idle_seconds=0
  )

  /**
   * Function to set app_has_focus to true
   */
  windowHasFocus = () => (this.app_has_focus =  true);
  /**
   * Function to set app_has_focus to false
   */
  windowLostFocus = () => (this.app_has_focus= false );

  /**
   * Function to check if idle timeout is reached to the inactivity timeout. If true redirect user to home page
   */
  refreshCheck = () => {
    const refresh_seconds = parseInt(this.refreshSeconds);
    if (
      this.idle_seconds >= refresh_seconds &&
      !this.app_has_focus &&
      document.readyState === "complete"
    ) {
      window.location.href = window.location.origin;
      this.resetIdleSeconds();
    }
  };

  timer = () => {
   this.idle_seconds = this.idle_seconds + 1;
     
    this.refreshCheck();
  }
  /**
   * Function to set event listeners on window and document to handle resetting of refresh timer.
   * This will be called only when `refreshSeconds` changes
   */
  setRefresher = () => {
    const interval = setInterval(this.timer, 1000);
    window.addEventListener("focus", this.windowHasFocus, false);
    window.addEventListener("blur", this.windowLostFocus, false);
    window.addEventListener("click", this.resetIdleSeconds, false);
    window.addEventListener("mousemove", this.resetIdleSeconds, false);
    window.addEventListener("mouseup", this.resetIdleSeconds, false);
    window.addEventListener("mousedown", this.resetIdleSeconds, false);
    window.addEventListener("keypress", this.resetIdleSeconds, false);
    window.addEventListener("scroll", this.resetIdleSeconds, false);
    document.addEventListener("touchMove", this.resetIdleSeconds, false);
    document.addEventListener("touchEnd", this.resetIdleSeconds, false);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", this.windowHasFocus, false);
      window.removeEventListener("blur", this.windowLostFocus, false);
      window.removeEventListener("click", this.resetIdleSeconds, false);
      window.removeEventListener("mousemove", this.resetIdleSeconds, false);
      window.removeEventListener("mouseup", this.resetIdleSeconds, false);
      window.removeEventListener("mousedown", this.resetIdleSeconds, false);
      window.removeEventListener("keypress", this.resetIdleSeconds, false);
      window.removeEventListener("scroll", this.resetIdleSeconds, false);
      document.removeEventListener("touchMove", this.resetIdleSeconds, false);
      document.removeEventListener("touchEnd", this.resetIdleSeconds, false);
    };
  };



  async loadMasterDataMount() {
    await this.loadAllProduct();
    await this.loadAllCountry();
    await this.loadAllExplanation();
    await this.loadAllStatus();
    await this.getAllUsers();
    await this.getAllActiveInactiveUsers();

  }
  getAllUsers = async () => {
    let response = await this.props.loadUsersData();
    this.setState(prevState => ({
      usersData: response.data.result
    })
    );
  }
  getAllActiveInactiveUsers = async () => {
    let response = await this.props.loadActiveInactiveUsersData();
    this.setState(prevState => ({
      usersActiveInactiveData: response.data.result
    })
    );
  }
  loadAllProduct = async () => {
    let response = await this.props.loadAllProduct();
    this.setState(
      { productData: response.data.result }
    );
  }

  loadAllCountry = async () => {
    let response = await this.props.loadAllCountry();
    this.setState(prevState => ({
      countryData: response.data.result
    })
    );
  }

  loadAllExplanation = async () => {
    let response = await this.props.loadAllExplanation();
    this.setState(prevState => ({
      explanationData: response.data.result
    })
    );
  }

  loadAllStatus = async () => {
    let response = await this.props.loadAllStatus();
    this.setState(prevState => ({
      statusData: response.data.result
    })
    );
  }

  /**
    * Function to post/fetch user activity details
  **/
  getUserActivityDetails = () => {
    const { loggedUserDetails, firstName, lastName } = this.state;
    const payload = loggedUserDetails.userDetail;
    fetch(`${config.Backend_URL}user/add`, {
      method: 'post',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' }
    }).then(response => response.json())
      .then(data => {
        this.setState({
          userActivityDetails: data.data
        });
        this.setLoader(false);
      }
      )
      .catch(error => {
        this.setLoader(false)
        swal(`${USER_ACTIVITY_ERROR_MESSAGE}:${error}`, {
          icon: "warning"
        });

      });
  }
  /**
     * Function to check user authentication
  **/
  getUserDetails = async () => {
    this.setLoader(true);
    let url = window.location.origin;
    await fetch(url + "/gettoken")
      .then(response => response.json())
      .then(data => {
        let admin = false;
        if (data.returnedObj.hasOwnProperty('roles')) {
          if (data.returnedObj.roles[0] != undefined && data.returnedObj.roles[0] == "LLP_ADMIN") {
            admin = true;
          }
        }
        this.setState({
          admin: admin,
          loggedUserDetails: data.returnedObj,
          firstName: data.first_name,
          lastName: data.last_name
        }, () => {
          this.getUserActivityDetails();
        });

      });
  }


  setLoader = isLoading => this.setState({ isLoading });

  render() {
    const { appEnvironment, appVersion, loggedUserDetails } = this.state;
    let localpath = localStorage.getItem("localpathname");
    return this.state.isLoading ? (
      <div className="loader">
        <FaSpinner />
      </div>
    ) : (this.state.restrictUser ? (
      <div className="unauthorized_Error">
        <h1>Unauthorized Access</h1>
        <p>
          You are not authorized to access this application.
          Please contact application <a href="mailto:" >Business Administrator </a> to get required access.
        </p>
      </div>
    ) : (
      <>
        <Header userDetails={loggedUserDetails.userDetail} />
        <main className="mainStyle">
          <Switch>
            <Route path="/auth/callback">
              <Redirect to={localpath} />
            </Route>
            <Route exact path="/help" component={Help} />
            <Route exact path="/" component={Dashboard} />
            <Route exact path="/dashboard" component={Dashboard} />
            <Route exact path="/dashboardresults/:site/:datatype" component={DashboardResults} />
            <Route exact path="/metricresults" component={MetricResults} />
            <Route exact path="/createNewAbstract"
              render={() => (loggedUserDetails.userDetail.role.includes('Business_Administrator') || loggedUserDetails.userDetail.role.includes('Literature_Associat')) ? (<CreateAbstract></CreateAbstract>
              ) : (<Redirect to="/notfound" />)} />
            <Route exact path="/abstractDetail/:abstractId" component={AbstractDetail} />

            <Route exact path="/search" component={Search} />
            <Route exact path="/searchresults" component={SearchResults} />
            <Route exact path="/notFound" component={NotFound} />
            <Route exact path="/audit/:abstractId" component={Audit} />
            <Route exact path="/abstracts" component={Abstracts} />
            <Route exact path="/searchResult/:searchId" component={AllSearchResults} />

            <Route exact path="/searchResult/:searchId/:title" component={AllSearchResults} />

            <Route exact path="/upload" render={() => (loggedUserDetails.userDetail.role === 'GLSD_Business_Administrator' || loggedUserDetails.userDetail.role === 'GLSD_Literature_Associate') ? (<UploadXMLFeed></UploadXMLFeed>
            ) : (<Redirect to="/notfound" />)} />

            <Route exact path="/metrics" render={() => (loggedUserDetails.userDetail.role !== 'GLSD_IT_Administrator') ? (<Metrics></Metrics>
            ) : (<Redirect to="/notfound" />)} />

            <Route exact path="/administration" render={() => (loggedUserDetails.userDetail.role === 'GLSD_Business_Administrator') ? (<Administration></Administration>
            ) : (<Redirect to="/notfound" />)} />
          </Switch>
          {/* <Loader /> */}
          <WarningModal />
        </main>
        <Footer env={appEnvironment} version={appVersion} />
      </>
    )
    );
  }
}


const mapStateToProps = state => {
  return {
    loggedUserDetails: state.userDataReducer.loggedUserDetails,
    usersData: state.userDataReducer.usersData,
    usersActiveInactiveData: state.userDataReducer.usersActiveInactiveData,
    productData: state.abstractDataReducer.productData,
    countryData: state.abstractDataReducer.countryData,
    statusData: state.abstractDataReducer.statusData,
    explanationData: state.abstractDataReducer.explanationData
  };
};
const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {

      loadAllProduct,
      loadAllCountry,
      loadAllExplanation,
      loadAllStatus,
      loadUsersData,
      loadActiveInactiveUsersData,
      createUser,
      loadLoggedUser,
      loadAuthProvider
    },
    dispatch
  );
};
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
